import { useState } from 'react';
import styled from 'foundations-theming-specialty/utils';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { FilterIcon } from 'foundations-assets/icons/components/FilterIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { Popover } from './atoms/Popover';
import { MobilePopover } from './MobilePopover';
import { FilterPopoverContent } from './TicketListingsFilterPopover';
import { useIsMobile } from '../hooks/useBreakpoint';
import { useTicketListingsSortAndFilters } from '../hooks/useTicketListingsSortAndFilters';
import { usageTracker } from '../utils/usageTracker';

// Positioned wrapper so the filter IconButton can sit visually inside the
// SearchInput's right edge. Custom CSS is scoped to layout only — every color,
// border, focus ring, and hover state comes from foundations + Trellis tokens.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SearchWithFilterWrapper = styled.div.withConfig({
  displayName: "TicketListingsSearchWithFilters__SearchWithFilterWrapper"
})(["position:relative;display:inline-block;inline-size:", ";"], ({
  $inlineSize
}) => $inlineSize);

// The IconButton sits on top of the SearchInput's right padding area. This
// styled-component handles positioning only; the button's visuals are owned by
// foundations IconButton.
const FilterTrigger = styled.div.withConfig({
  displayName: "TicketListingsSearchWithFilters__FilterTrigger"
})(["position:absolute;inset-block:0;inset-inline-end:", ";display:flex;align-items:center;"], ({
  theme
}) => theme.space[100]);

// Small dot at the top-right of the filter button indicating active filters.
// Uses Trellis brand token so theming/dark-mode overrides flow through.
const ActiveFilterBadge = styled.span.withConfig({
  displayName: "TicketListingsSearchWithFilters__ActiveFilterBadge"
})(["position:absolute;inset-block-start:", ";inset-inline-end:", ";inline-size:", ";block-size:", ";background-color:", ";border-radius:50%;border:2px solid ", ";pointer-events:none;"], ({
  theme
}) => theme.space[250], ({
  theme
}) => theme.space[100], ({
  theme
}) => theme.space[250], ({
  theme
}) => theme.space[250], ({
  theme
}) => theme.color['text-interactive-default'], ({
  theme
}) => theme.color['text-core-default']);
/**
 * Combobox-style search + filter trigger for the ticket listings page.
 *
 * - The SearchInput keeps all behavior from `TicketListingsSearch`: local
 *   typing buffer, commit on Enter, external-clear sync, built-in × clear.
 * - A filter IconButton sits inside the input's right edge.
 * - Clicking the button opens a Popover anchored under the input that matches
 *   the input's width and hosts the existing `FilterPopoverContent`.
 *
 * Theming: every visual surface (input border/background, button hover/focus,
 * popover surface) is owned by foundations components and Trellis tokens.
 * The only custom CSS is positioning — no hard-coded colors.
 */
export function TicketListingsSearchWithFilters({
  screen,
  inlineSize = '100%',
  marginInlineEnd,
  showOwnershipFilter = false
}) {
  const {
    searchString,
    setSearchString,
    activeFilterCount,
    resetFilters
  } = useTicketListingsSortAndFilters();
  const isMobile = useIsMobile();

  // Local input state so typing doesn't push to global/URL on every keystroke.
  const [searchTerm, setSearchTerm] = useState(searchString);
  const [prevSearchString, setPrevSearchString] = useState(searchString);
  if (searchString !== prevSearchString) {
    // External clear (Clear all, URL change, etc.) — mirror it into the input.
    setPrevSearchString(searchString);
    setSearchTerm(searchString);
  }
  const [filterOpen, setFilterOpen] = useState(false);
  const filterButtonLabel = unescapedText('customer-portal-app.filters.buttonLabelWithCount', {
    count: activeFilterCount
  });
  const searchInputAndFilterTrigger = /*#__PURE__*/_jsxs(SearchWithFilterWrapper, {
    $inlineSize: inlineSize,
    children: [/*#__PURE__*/_jsx(SearchInput, {
      "data-test-id": "search-input"
      // Disable the built-in clear (×) button — SearchInput positions it in
      // the same right-edge slot as our filter IconButton, causing overlap.
      ,
      clearable: false
      // Hide the built-in magnifying glass — our filter IconButton sits in
      // the same right-edge slot and would visually collide with it.
      ,
      icon: false,
      inlineSize: "100%",
      marginInlineEnd: marginInlineEnd,
      "aria-label": I18n.text('customer-portal-app.search.description'),
      placeholder: I18n.text('customer-portal-app.search.placeholder'),
      value: searchTerm,
      onChange: e => {
        if (e.target.value === '') setSearchString('');
        setSearchTerm(e.target.value);
      },
      onKeyDown: e => {
        if (e.key === 'Enter') {
          usageTracker.track('Customer Portal App', 'Tickets Discovered', {
            action: 'tickets-searched',
            screen
          });
          setSearchString(searchTerm);
        }
      }
    }), /*#__PURE__*/_jsxs(FilterTrigger, {
      children: [/*#__PURE__*/_jsx(IconButton, {
        use: "secondary-alt",
        size: "sm",
        shape: "circle",
        borderWidth: "0",
        tooltip: I18n.text('customer-portal-app.filtersTooltip'),
        "data-test-id": "filter-button",
        "aria-label": filterButtonLabel,
        "aria-expanded": filterOpen,
        "aria-haspopup": "dialog",
        onClick: () => {
          if (!filterOpen) {
            usageTracker.track('Customer Portal App', 'Tickets Discovered', {
              action: 'ticket-filter-discovered',
              screen
            });
          }
          setFilterOpen(!filterOpen);
        },
        children: /*#__PURE__*/_jsx(FilterIcon, {
          color: "icon-core-subtle"
        })
      }), activeFilterCount > 0 && /*#__PURE__*/_jsx(ActiveFilterBadge, {
        "aria-hidden": "true",
        "data-test-id": "active-filter-badge"
      })]
    })]
  });
  const popoverContent = /*#__PURE__*/_jsx(FilterPopoverContent, {
    showOwnershipFilter: showOwnershipFilter
  });
  if (isMobile) {
    return /*#__PURE__*/_jsx(MobilePopover, {
      title: I18n.text('customer-portal-app.filters.searchOptions'),
      open: filterOpen,
      onOpenChange: setFilterOpen,
      content: popoverContent,
      footer: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        children: /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            use: "transparent",
            onClick: () => {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'tickets-filter-cleared',
                screen
              });
              resetFilters();
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.filters.reset"
            })
          })
        })
      }),
      children: searchInputAndFilterTrigger
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: "none",
    open: filterOpen,
    placement: "bottom right",
    onOpenChange: e => setFilterOpen(e.currentTarget.value),
    content: popoverContent,
    children: searchInputAndFilterTrigger
  });
}